export type TransferPackNumpadFillFrom = "sm" | "lg" | "xl";

const NUMPAD_FILL_BUTTON = {
  sm: "h-auto min-h-12 touch-manipulation sm:h-full text-lg font-semibold",
  lg: "h-auto min-h-11 touch-manipulation lg:h-full lg:min-h-0 text-lg font-semibold",
  xl: "h-auto min-h-11 touch-manipulation xl:h-full xl:min-h-0 text-lg font-semibold",
} as const;

const NUMPAD_FILL_DIGIT = {
  sm: "h-auto min-h-12 touch-manipulation sm:h-full text-2xl font-semibold tabular-nums",
  lg: "h-auto min-h-11 touch-manipulation lg:h-full lg:min-h-0 text-2xl font-semibold tabular-nums",
  xl: "h-auto min-h-11 touch-manipulation xl:h-full xl:min-h-0 text-xl font-semibold tabular-nums",
} as const;

const NUMPAD_FILL_ENTER = {
  sm: "h-auto min-h-12 touch-manipulation sm:h-full text-xl font-semibold",
  lg: "h-auto min-h-11 touch-manipulation lg:h-full lg:min-h-0 text-xl font-semibold",
  xl: "h-auto min-h-11 touch-manipulation xl:h-full xl:min-h-0 text-lg font-semibold",
} as const;

const NUMPAD_FILL_GRID = {
  sm: "h-full min-h-0 sm:grid-rows-[repeat(5,minmax(3rem,1fr))]",
  lg: "lg:h-full lg:min-h-0 lg:grid-rows-[repeat(5,minmax(0,1fr))]",
  xl: "xl:h-full xl:min-h-0 xl:grid-rows-[repeat(5,minmax(2.75rem,1fr))]",
} as const;

export function transferPackNumpadFillClasses(from: TransferPackNumpadFillFrom): {
  buttonClass: string;
  digitClass: string;
  enterClass: string;
  gridClass: string;
} {
  return {
    buttonClass: NUMPAD_FILL_BUTTON[from],
    digitClass: NUMPAD_FILL_DIGIT[from],
    enterClass: NUMPAD_FILL_ENTER[from],
    gridClass: NUMPAD_FILL_GRID[from],
  };
}

/** List + numpad side by side from sm. Tablet keeps a narrower keypad so SKU tiles can wrap. */
export function kioskNumpadSplitGridClass(numpadOnRight: boolean): string {
  const columns = numpadOnRight
    ? "sm:grid-cols-[minmax(0,1fr)_minmax(15rem,16.5rem)] sm:grid-rows-[minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_minmax(17rem,20rem)]"
    : "sm:grid-cols-[minmax(15rem,16.5rem)_minmax(0,1fr)] sm:grid-rows-[minmax(0,1fr)] xl:grid-cols-[minmax(17rem,20rem)_minmax(0,1fr)]";
  return [
    "grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] gap-2 overflow-hidden pt-2",
    columns,
  ].join(" ");
}

/** Picking tablet keypad column (15.5rem to 18rem). Do not narrow the cart or grow the ten-key until a Tab M9 floor trial. See device-management docs/android/HUB-VIEWPORT.md hold-off note. */
export function kittingPickNumpadSplitGridClass(numpadOnRight: boolean): string {
  const columns = numpadOnRight
    ? "sm:grid-cols-[minmax(0,1fr)_minmax(15.5rem,18rem)] sm:grid-rows-[minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_minmax(17rem,20rem)]"
    : "sm:grid-cols-[minmax(15.5rem,18rem)_minmax(0,1fr)] sm:grid-rows-[minmax(0,1fr)] xl:grid-cols-[minmax(17rem,20rem)_minmax(0,1fr)]";
  return [
    "grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)_minmax(0,1fr)] gap-2 overflow-hidden pt-2",
    columns,
  ].join(" ");
}

export function kioskMainPaneOrderClass(numpadOnRight: boolean): string {
  return numpadOnRight ? "order-1" : "order-2";
}

export function kioskNumpadPaneOrderClass(numpadOnRight: boolean): string {
  return numpadOnRight ? "order-2" : "order-1";
}

export type KittingPickNumpadCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export const KITTING_PICK_NUMPAD_CORNER_STORAGE_KEY = "kitting-pick-numpad-corner";

export function kittingPickNumpadOnRight(corner: KittingPickNumpadCorner): boolean {
  return corner === "top-right" || corner === "bottom-right";
}

export function kittingPickNumpadOnBottom(corner: KittingPickNumpadCorner): boolean {
  return corner === "bottom-left" || corner === "bottom-right";
}

export function readStoredKittingPickNumpadCorner(): KittingPickNumpadCorner {
  try {
    const stored = window.localStorage.getItem(KITTING_PICK_NUMPAD_CORNER_STORAGE_KEY);
    if (
      stored === "top-left"
      || stored === "top-right"
      || stored === "bottom-left"
      || stored === "bottom-right"
    ) {
      return stored;
    }
    const legacySide = window.localStorage.getItem("prepack-transfer-numpad-side");
    if (legacySide === "left") return "bottom-left";
    if (legacySide === "right") return "bottom-right";
  } catch {
    /* ignore */
  }
  return "bottom-right";
}

/**
 * Touch tablets often drop `click` after `pointerdown` preventDefault (focus guard).
 * Fire the action on the primary pointer press instead of waiting for click.
 */
export function numpadActivateFromPointerDown(
  event: { button: number; preventDefault: () => void },
  action: () => void,
): void {
  event.preventDefault();
  if (event.button !== 0) return;
  action();
}
